import React, { PropsWithChildren, useContext, useEffect, useState } from 'react'
import WebSpinner from '../spinner/web-spinner'

/**
 * LoadingProvider 컴포넌트
 * - Tailwind CSS 기반 로딩 상태 관리
 * - Context API를 통한 전역 로딩 상태 제공
 */

interface LoadingContextType {
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
}

export const LoadingContext = React.createContext<LoadingContextType | null>(null)

/**
 * Loading 컴포넌트
 * - WebSpinner를 포함한 로딩 오버레이
 */
export const Loading = () => {
  return (
    <div className="fixed left-0 top-0 z-[9999] flex h-full w-full items-center justify-center">
      <WebSpinner />
    </div>
  )
}

/**
 * LoadingProvider 컴포넌트
 * - 자식 컴포넌트에 로딩 상태를 제공
 * - isLoading이 true일 때 로딩 스피너 표시
 */
const LoadingProvider = function ({ children }: PropsWithChildren) {
  const [isLoading, setIsLoading] = useState(false)
  return (
    <LoadingContext.Provider value={{ isLoading, setIsLoading }}>
      {children}
      {isLoading && <Loading />}
    </LoadingContext.Provider>
  )
}

export default LoadingProvider

/**
 * 로딩 컴포넌트를 설정하기 위한 hook
 * @param loading - 로딩 여부
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const [isLoading, setIsLoading] = useState(false)
 *   useLoading(isLoading)
 *   return <div>...</div>
 * }
 * ```
 */
export const useLoading = function (loading: boolean) {
  const ctx = useContext(LoadingContext)
  if (!ctx) {
    throw new Error('useLoading must be used within LoadingProvider')
  }
  const { setIsLoading } = ctx
  useEffect(() => {
    setIsLoading(loading)
    return () => setIsLoading(false)
  }, [loading, setIsLoading])
  return ctx
}
